import React, { useState } from 'react';
import { contactPage, siteConfig } from '../data/mockData';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: contactPage.form.fields.subject.options[0],
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));

    setSubmitting(false);
    setSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: contactPage.form.fields.subject.options[0],
      message: ''
    });
  };

  return (
    <div className="bg-background-light min-h-screen flex flex-col">
      {/* Hero Section */}
      <section className="bg-white py-12 lg:py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight tracking-tight mb-4">
              {contactPage.hero.title}
            </h1>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed">
              {contactPage.hero.description}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 lg:py-16 flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            {/* Form Column */}
            <div className="lg:col-span-7 bg-white p-7 lg:p-8 rounded-xl border border-slate-200">
              <h2 className="text-xl font-semibold mb-6 flex items-center gap-2 text-slate-900">
                <span className="material-symbols-outlined text-primary text-xl">mail</span>
                {contactPage.form.title}
              </h2>

              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <span className="material-symbols-outlined text-green-600 text-3xl mb-2">check_circle</span>
                  <h3 className="text-base font-semibold text-green-800 mb-2">Thank you for your message!</h3>
                  <p className="text-green-700 text-sm">We will get back to you as soon as possible.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-4 text-primary font-medium hover:underline text-sm"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700">
                        {contactPage.form.fields.name.label} *
                      </label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full rounded-md border-slate-300 focus:ring-primary focus:border-primary px-4 py-2.5 border"
                        placeholder={contactPage.form.fields.name.placeholder}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                        {contactPage.form.fields.email.label} *
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full rounded-md border-slate-300 focus:ring-primary focus:border-primary px-4 py-2.5 border"
                        placeholder={contactPage.form.fields.email.placeholder}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700">
                        {contactPage.form.fields.phone.label} *
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full rounded-md border-slate-300 focus:ring-primary focus:border-primary px-4 py-2.5 border"
                        placeholder={contactPage.form.fields.phone.placeholder}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="subject" className="block text-sm font-medium text-slate-700">
                        {contactPage.form.fields.subject.label}
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full rounded-md border-slate-300 focus:ring-primary focus:border-primary px-4 py-2.5 border"
                      >
                        {contactPage.form.fields.subject.options.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700">
                      {contactPage.form.fields.message.label} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={contactPage.form.fields.message.rows}
                      className="w-full rounded-md border-slate-300 focus:ring-primary focus:border-primary px-4 py-2.5 border"
                      placeholder={contactPage.form.fields.message.placeholder}
                    />
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="material-symbols-outlined text-sm">info</span>
                    {contactPage.form.privacyNote}
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-primary text-white py-3.5 rounded-md font-medium text-base hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <span className="material-symbols-outlined animate-spin">refresh</span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-xl">send</span>
                        {contactPage.form.submitButton}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Details Column */}
            <div className="lg:col-span-5 space-y-6">
              {/* Location Cards */}
              {contactPage.locations.map((location) => (
                <div key={location.city} className="bg-white rounded-xl overflow-hidden border border-slate-200">
                  <div className="h-28 bg-slate-200 flex items-center justify-center relative">
                    <img
                      src={location.image}
                      alt={`${location.city} location`}
                      className="w-full h-full object-cover opacity-40 grayscale"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="bg-white/95 px-4 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wide text-primary">
                        {location.label}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 space-y-3.5">
                    <div className="flex gap-3">
                      <span className="material-symbols-outlined text-primary text-xl flex-shrink-0 mt-0.5">location_on</span>
                      <div>
                        <p className="font-semibold text-slate-900">{location.city} Branch</p>
                        <p className="text-sm text-slate-600 leading-relaxed">{location.address}</p>
                      </div>
                    </div>
                    <div className="flex gap-3 items-center">
                      <span className="material-symbols-outlined text-primary text-xl flex-shrink-0">call</span>
                      <p className="text-sm text-slate-600">{location.phone}</p>
                    </div>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(location.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-2.5 text-sm font-medium border border-slate-300 text-primary hover:bg-primary/5 rounded-md transition-colors flex items-center justify-center gap-1.5"
                    >
                      <span className="material-symbols-outlined text-sm">map</span>
                      View on Google Maps
                    </a>
                  </div>
                </div>
              ))}

              {/* Business Info */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Registration Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Provider Number:</span>
                    <span className="font-mono font-semibold text-primary">{siteConfig.registration.providerNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">ABN:</span>
                    <span className="font-mono font-semibold text-primary">{siteConfig.registration.abn}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-slate-200">
                    <span className="text-slate-600">Business Hours:</span>
                    <span className="font-medium text-slate-900">{siteConfig.contact.hours}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-20 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">{contactPage.faq.title}</h2>
            <p className="text-slate-600">{contactPage.faq.subtitle}</p>
          </div>
          <div className="space-y-3">
            {contactPage.faq.items.map((item, index) => (
              <div key={index} className="border border-slate-200 rounded-lg overflow-hidden">
                <details className="group">
                  <summary className="w-full flex justify-between items-center p-4 text-left bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer list-none">
                    <span className="font-medium text-slate-900 pr-4">{item.question}</span>
                    <span className="material-symbols-outlined text-primary flex-shrink-0 group-open:rotate-180 transition-transform">expand_more</span>
                  </summary>
                  <div className="p-4 text-slate-600 text-sm leading-relaxed bg-white border-t border-slate-100">
                    {item.answer}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
